import React, { ReactElement, useEffect, useState } from 'react';
import { LiveProps } from '../../../LiveContainer';
import './LiveHeaderMobile.scss';
import {
  useEndDeliveryLiveMutation,
  useEndViewLiveMutation,
  useGetLiveCoinMutation,
  useGetPersonalDataMutation,
  usePostFollowMutation,
} from '../../../../../services/api/apiProvider';
import { RtmMessage } from 'agora-rtm-sdk';
import { convertTime, getLevelColor } from '../../../../../common/utils/utils';
import { MessageType } from '../../../types/messageType';
import { Debug } from '../../../../../common/utils/debugLogs';
import { MessageUser } from '../../../models/messageUser';
import { ImageCustom } from '../../../../../common/components/ImageCustom';
import diamondIcon from '../../../../../assets/png/diamond.png';
import { AiOutlineClose } from 'react-icons/ai';
import LiveAudienceMobileModal from '../LiveAudienceMobile/LiveAudienceMobileModal';
import { useDispatch } from 'react-redux';
import { actions } from '../../../liveSlice';

const LiveHeaderMobile = (props: LiveProps): ReactElement => {
  const dispatch = useDispatch();

  // live score
  const [score, setScore] = useState<number | undefined>(
    props.liveState.liveData?.score,
  );

  // live time
  const [liveTimeSec, setLiveTimeSec] = useState<number>(0);

  // live coin
  const [coin, setCoin] = useState<number | undefined>();
  const [getLiveCoin] = useGetLiveCoinMutation();

  // follow liver
  const [followed, setFollowed] = useState<boolean>();
  const [postFollow] = usePostFollowMutation();
  const [getLiverPersonalData] = useGetPersonalDataMutation();
  const [endView] = useEndViewLiveMutation();
  const [stopDelivery] = useEndDeliveryLiveMutation();

  /**
   * Side effect for counting of live time
   */
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (props.liveState.isInLive && props.liveState.liveData) {
      interval = setInterval(() => {
        setLiveTimeSec(getLiveTime());
      }, 1000);
    }

    return () => {
      clearInterval(interval);
    };
  });

  useEffect(() => {
    if (props.liveState.isInLive && props.liveState.liveData) {
      setLiveTimeSec(getLiveTime());
      setScore(props.liveState.liveData.score);
      fetchCoinData();
      if (props.liveState.role === 'audience') {
        fetchLiverPersonalData();
      }
      listenChannelMessage();
    }
  }, [props.liveState.isInLive]);

  /**
   * Fetch live coin data from API.
   */
  const fetchCoinData = () => {
    if (props.liveState.liveData) {
      getLiveCoin(props.liveState.liveData.id)
        .unwrap()
        .then((data) => {
          setCoin(data.coin);
        });
    }
  };

  /**
   * Fetch live coin data from API.
   */
  const fetchLiverPersonalData = async () => {
    if (props.liveState.liveData) {
      const liverId = props.liveState.liveData.user.id;
      const liverData = await getLiverPersonalData(liverId).unwrap();
      if (liverData) {
        setFollowed(liverData.followStatus === 'following');
      }
    }
  };

  /**
   * Get live time second
   */
  const getLiveTime = (): number => {
    if (props.liveState.liveData) {
      const now = new Date();
      const startTime = new Date(props.liveState.liveData.startDateTime);
      const time = Math.floor((now.getTime() - startTime.getTime()) / 1000);
      return time;
    } else {
      return 0;
    }
  };

  /**
   * Listen RTM message from joined channel
   * Handle messages: Notify score, Notify coin.
   */
  const listenChannelMessage = () => {
    props.liveState.rtmEngine?.on(
      'ChannelMessage',
      (message: RtmMessage): void => {
        if (message.messageType === 'TEXT') {
          const content = JSON.parse(message.text);

          // New score
          if (content.type === MessageType.NOTIFY_SCORE) {
            const newScore = content.value.score as number;
            if (!newScore) {
              return;
            }

            Debug.log('NEW SCORE ' + newScore);
            setScore(newScore);
          }

          // New coin
          if (content.type === MessageType.NOTIFY_BERRY_COIN) {
            const newCoin = content.value.coin as number;
            if (!newCoin) {
              return;
            }

            Debug.log('NEW COIN ' + newCoin);
            setCoin(newCoin);
          }

          // Level Up
          if (content.type === MessageType.NOTIFY_LIVER_LEVEL_UP) {
            try {
              const user = content.value as MessageUser;
              props.updateLiverLevel(user);
            } catch (error) {
              Debug.log(`Fail to parse notify message: ${error}`);
            }
          }
        }
      },
    );
  };
  const btnEndLiveOnClick = async () => {
    // end delivery live
    if (
      props.liveState.role === 'host' &&
      props.liveState.liveData !== undefined
    ) {
      // end live and show result
      await stopDelivery(props.liveState.liveData.id).unwrap();
      props.endLive();
    }

    // end view live
    if (
      props.liveState.role === 'audience' &&
      props.liveState.liveData !== undefined
    ) {
      await endView(props.liveState.liveData.id).unwrap();
      window.location.href = `${window.location.origin}/${process.env.REACT_APP_BASE_URL}`;
    }
  };
  const handleOpenAudiencesModal = () => {
    dispatch(actions.toggleAudienceMobileModal(true));
  };
  const handleFollowClick = async () => {
    if (!props.liveState.liveData) {
      return;
    }
    const userId = props.liveState.liveData.user.id;
    const response = await postFollow(userId).unwrap();
    if (response) {
      setFollowed(true);
    }
  };

  return (
    <div className="live-mobile-header-root">
      <div className="live-mobile-header-root__top">
        <div className="live-mobile-header__score">
          <span>スコア</span>
          <span className="live-record__stat">
            {' '}
            {score?.toLocaleString() ?? ''}
          </span>
        </div>
        {props.liveState.role === 'audience' && followed === false && (
          <div className="live-record__follow-btn" onClick={handleFollowClick}>
            フォロー
          </div>
        )}
        <div className="live-mobile-header__timer">
          <span>{liveTimeSec ? convertTime(liveTimeSec) : ''}</span>
          <span
            onClick={btnEndLiveOnClick}
            className="live-mobile-header__end-live"
          >
            <AiOutlineClose />
          </span>
        </div>
      </div>
      <div className="live-mobile-header__user">
        <div className="live-mobile-header__user-avatar">
          <ImageCustom src={props.liveState.liveData?.user?.picture?.uri} />
        </div>
        <div
          className="live-mobile-header__user-name"
          style={{
            color: getLevelColor(props.liveState.liverLv),
          }}
        >
          {' '}
          {props.liveState.liveData?.user.loginId}
        </div>
        <div
          className="live-mobile-header__user-level"
          style={{
            backgroundColor: getLevelColor(props.liveState.liverLv),
          }}
        >
          {`Lv.${props.liveState.liverLv}`}
        </div>
      </div>
      <div className="live-mobile-header__audience">
        <div
          onClick={handleOpenAudiencesModal}
          className="live-mobile-header__audience-list"
          style={{
            backgroundColor: getLevelColor(props.liveState.liverLv),
          }}
        >
          リスナー
        </div>
        <div className="live-mobile-header__user-diamond">
          <div className="live-mobile-header__user-diamond-image">
            <ImageCustom
              className="app-bar__user__diamond-icon"
              src={diamondIcon}
            />
          </div>
          <div className="live-mobile-header__user-diamond-coin">
            {coin?.toLocaleString() ?? ''}
          </div>
        </div>
      </div>
      <LiveAudienceMobileModal {...props} />
    </div>
  );
};
export default LiveHeaderMobile;
